/**
 * Inventory Picker Contract — PRD.188
 *
 * Shared types, queries, and helpers for selecting Medicine inventory items
 * from future prescription, invoice, and pharmacy-dispense workflows.
 *
 * Boundaries (do NOT change without a new PRD):
 *   - This module is read-only against the inventory_* tables.
 *   - It does not import or reference prescription / invoice / pharmacy /
 *     stock-movement tables.
 *   - It does not deduct stock — stock movement belongs to a future PRD.
 *   - Free-text/manual entry MUST remain available in any consumer workflow;
 *     the picker is one of two valid input modes (the other being free text).
 *
 * Snapshot rule: when a downstream record (prescription line, invoice line,
 * pharmacy dispense line) references an inventory item, it should also copy
 * display values into snapshot columns so historical records survive item
 * edits and price changes. The `to*Snapshot` helpers below produce those
 * snapshot payloads from a picker selection.
 */

import type { QuerySpec } from '@helios/powersync-client-core';
import type { InventoryItemRow, InventoryMedicineDetailRow } from './inventory-items';

// ─── Picker selection (returned by the future picker component) ───

/**
 * Shape returned by a future InventoryItemPicker (PRD.188 §7).
 * Carries both the item id and the snapshot candidate values that downstream
 * line-item records will copy into their own snapshot columns.
 *
 * `currentStockDisplay` is informational only until stock movement logic
 * exists (PRD.191). It uses initial_stock_quantity as a temporary stand-in
 * and must not be treated as a true balance.
 */
export interface InventoryPickerSelection {
  inventoryItemId: string;
  // PRD.205 widens this to include 'product' alongside 'medicine'. Other
  // future item types (supply / equipment / etc.) are not enabled yet.
  itemType: 'medicine' | 'product';
  itemName: string;
  sku: string | null;
  category: string | null;
  status: string | null;

  // Pricing snapshot candidates
  sellingPrice: number | null;
  minimumSellingPrice: number | null;
  costPrice: number | null;
  openPricing: boolean;
  taxInclusive: boolean;

  // Unit snapshot candidates
  inventoryUnit: string | null;
  purchasingUnit: string | null;
  packSizeQuantity: number | null;

  // Stock display only — NOT a real balance
  currentStockDisplay: number | null;
  lowStockLimit: number | null;
  trackStock: boolean;

  // Medicine-specific snapshot candidates
  genericName: string | null;
  brandName: string | null;
  manufacturer: string | null;
  strength: string | null;
  dosageForm: string | null;
  route: string | null;
  defaultInstruction: string | null;
  defaultDosage: string | null;
  usage: string | null;
  dose: string | null;
  doseUnit: string | null;
  frequency: string | null;
  duration: string | null;
}

// ─── Snapshot shapes for downstream consumers ───

/**
 * Snapshot payload for a future prescription line that references an
 * inventory item. Downstream tables (PRD.189) should store these values
 * alongside an optional `inventory_item_id` so historical prescriptions
 * remain accurate even when the catalog item changes.
 *
 * Free-text-only prescriptions should leave `inventory_item_id` null and
 * may populate only `item_name_snapshot` from the user-typed name.
 */
export interface PrescriptionLineSnapshot {
  inventory_item_id: string;
  item_name_snapshot: string;
  sku_snapshot: string | null;
  generic_name_snapshot: string | null;
  brand_name_snapshot: string | null;
  strength_snapshot: string | null;
  dosage_form_snapshot: string | null;
  route_snapshot: string | null;
  unit_snapshot: string | null;
  default_instruction_snapshot: string | null;
  default_dosage_snapshot: string | null;
  usage_snapshot: string | null;
  dose_snapshot: string | null;
  dose_unit_snapshot: string | null;
  frequency_snapshot: string | null;
  duration_snapshot: string | null;
}

/**
 * Snapshot payload for a future invoice line that references an inventory
 * item. Downstream tables (PRD.190) should store these values alongside an
 * optional `inventory_item_id` so historical invoices remain accurate even
 * when prices or names change.
 *
 * Free-text-only invoice lines should leave `inventory_item_id` null and
 * may populate only `description_snapshot` from the user-typed text.
 */
export interface InvoiceLineSnapshot {
  inventory_item_id: string;
  description_snapshot: string;
  sku_snapshot: string | null;
  unit_snapshot: string | null;
  unit_price_snapshot: number | null;
  tax_inclusive_snapshot: boolean;
  open_pricing_snapshot: boolean;
}

// ─── Mappers ───

/**
 * Build an `InventoryPickerSelection` from an inventory_items row and its
 * (optional) inventory_medicine_details row. Use this in the future picker
 * component when the user picks a row from search results.
 */
export function toInventoryPickerSelection(
  item: InventoryItemRow,
  detail: InventoryMedicineDetailRow | null,
): InventoryPickerSelection {
  // PRD.205 — read the item type from the row so Product candidates report
  // `itemType: 'product'`. Default to 'medicine' for null/legacy rows so
  // Medicine-only consumers (Pharmacy) still see the expected value.
  const itemType: 'medicine' | 'product' = item.item_type === 'product' ? 'product' : 'medicine';
  return {
    inventoryItemId: item.id,
    itemType,
    itemName: item.item_name ?? '',
    sku: item.sku,
    category: item.category,
    status: item.status,

    sellingPrice: item.selling_price,
    minimumSellingPrice: item.minimum_selling_price,
    costPrice: item.cost_price,
    openPricing: item.open_pricing === 1,
    taxInclusive: item.tax_inclusive === 1,

    inventoryUnit: item.inventory_unit,
    purchasingUnit: item.purchasing_unit,
    packSizeQuantity: item.pack_size_quantity,

    currentStockDisplay: item.initial_stock_quantity,
    lowStockLimit: item.low_stock_limit,
    trackStock: item.track_stock === 1,

    genericName: detail?.generic_name ?? null,
    brandName: detail?.brand_name ?? null,
    manufacturer: detail?.manufacturer ?? null,
    strength: detail?.strength ?? null,
    dosageForm: detail?.dosage_form ?? null,
    route: detail?.route ?? null,
    defaultInstruction: detail?.default_instruction ?? null,
    defaultDosage: detail?.default_dosage ?? null,
    usage: detail?.usage ?? null,
    dose: detail?.dose ?? null,
    doseUnit: detail?.dose_unit ?? null,
    frequency: detail?.frequency ?? null,
    duration: detail?.duration ?? null,
  };
}

/**
 * Convert an `InventoryPickerSelection` into the snapshot fields a future
 * prescription line (PRD.189) should store.
 *
 * IMPORTANT: a prescription line should ALSO write its own `instruction`,
 * `dose`, `quantity`, etc. from the form (which may have been edited away
 * from the medicine defaults). The `*_snapshot` fields are the historical
 * record of what the catalog item looked like at the time of prescribing.
 */
export function toPrescriptionLineSnapshot(
  selection: InventoryPickerSelection,
): PrescriptionLineSnapshot {
  return {
    inventory_item_id: selection.inventoryItemId,
    item_name_snapshot: selection.itemName,
    sku_snapshot: selection.sku,
    generic_name_snapshot: selection.genericName,
    brand_name_snapshot: selection.brandName,
    strength_snapshot: selection.strength,
    dosage_form_snapshot: selection.dosageForm,
    route_snapshot: selection.route,
    unit_snapshot: selection.inventoryUnit,
    default_instruction_snapshot: selection.defaultInstruction,
    default_dosage_snapshot: selection.defaultDosage,
    usage_snapshot: selection.usage,
    dose_snapshot: selection.dose,
    dose_unit_snapshot: selection.doseUnit,
    frequency_snapshot: selection.frequency,
    duration_snapshot: selection.duration,
  };
}

/**
 * Convert an `InventoryPickerSelection` into the snapshot fields a future
 * invoice line (PRD.190) should store.
 *
 * `unit_price_snapshot` defaults to the item's selling price. The invoice
 * line should also store its own `unit_price` (which may be edited at
 * billing time, e.g. when `openPricing` is true).
 */
export function toInvoiceLineSnapshot(selection: InventoryPickerSelection): InvoiceLineSnapshot {
  return {
    inventory_item_id: selection.inventoryItemId,
    description_snapshot: selection.itemName,
    sku_snapshot: selection.sku,
    unit_snapshot: selection.inventoryUnit,
    unit_price_snapshot: selection.sellingPrice,
    tax_inclusive_snapshot: selection.taxInclusive,
    open_pricing_snapshot: selection.openPricing,
  };
}

// ─── Picker candidate query ───

/**
 * Row shape returned by `qInventoryPickerCandidates` — a flat join of
 * inventory_items and (optional) inventory_medicine_details, scoped to
 * Medicine items. All inventory_items columns are kept; medicine columns
 * are prefixed with `med_` so consumers can disambiguate.
 *
 * Use `pickerCandidateRowToSelection` to map a row into an
 * `InventoryPickerSelection` for the picker callback.
 */
export interface InventoryPickerCandidateRow extends InventoryItemRow {
  med_drug_classification: string | null;
  med_brand_name: string | null;
  med_generic_name: string | null;
  med_manufacturer: string | null;
  med_indication: string | null;
  med_strength: string | null;
  med_dosage_form: string | null;
  med_route: string | null;
  med_default_instruction: string | null;
  med_default_dosage: string | null;
  med_usage: string | null;
  med_dose: string | null;
  med_dose_unit: string | null;
  med_frequency: string | null;
  med_duration: string | null;
}

/**
 * Default picker query: active inventory items of the requested types,
 * joined with their (optional) medicine details, with optional fuzzy
 * search across item_name, sku, generic_name, brand_name, and strength.
 *
 * `itemTypes` defaults to `['medicine']` so existing Medicine-only callers
 * (Pharmacy, Medicine prescription/invoice flows pre-PRD.205) keep their
 * Medicine-scoped behavior. Pass `['medicine', 'product']` (PRD.205) to
 * surface Product candidates alongside Medicine for invoice unified
 * search and dermatology prescription/care-plan search.
 *
 * Pass `search` as an empty string for the unfiltered list. Result count
 * is capped at 50 to keep the picker responsive — the picker UI should
 * debounce the search input.
 */
export function qInventoryPickerCandidates(
  search: string,
  itemTypes: readonly string[] = ['medicine'],
): QuerySpec<unknown[]> {
  const term = `%${search.trim().toLowerCase()}%`;
  // Build a positional `IN (?, ?, ...)` clause so we can extend to Product
  // (and any future enabled type) without baking the type list into SQL.
  const typePlaceholders = itemTypes.length > 0 ? itemTypes.map(() => '?').join(', ') : '?';
  const safeTypes = itemTypes.length > 0 ? itemTypes : ['medicine'];
  return {
    key: ['inventory-picker-candidates', search, ...safeTypes] as const,
    sql: `
      SELECT
        i.*,
        m.drug_classification AS med_drug_classification,
        m.brand_name          AS med_brand_name,
        m.generic_name        AS med_generic_name,
        m.manufacturer        AS med_manufacturer,
        m.indication          AS med_indication,
        m.strength            AS med_strength,
        m.dosage_form         AS med_dosage_form,
        m.route               AS med_route,
        m.default_instruction AS med_default_instruction,
        m.default_dosage      AS med_default_dosage,
        m.usage               AS med_usage,
        m.dose                AS med_dose,
        m.dose_unit           AS med_dose_unit,
        m.frequency           AS med_frequency,
        m.duration            AS med_duration
      FROM inventory_items i
      LEFT JOIN inventory_medicine_details m
        ON m.inventory_item_id = i.id
      WHERE i.item_type IN (${typePlaceholders})
        AND COALESCE(i.inactive, 0) = 0
        AND COALESCE(i.status, 'Active') != 'Inactive'
        AND (
          ? = '%%'
          OR LOWER(COALESCE(i.item_name, '')) LIKE ?
          OR LOWER(COALESCE(i.sku, ''))       LIKE ?
          OR LOWER(COALESCE(m.generic_name, '')) LIKE ?
          OR LOWER(COALESCE(m.brand_name, ''))   LIKE ?
        )
      ORDER BY i.item_name ASC
      LIMIT 50
    `,
    params: [...safeTypes, term, term, term, term, term],
    watch: ['inventory_items', 'inventory_medicine_details'],
  };
}

/**
 * Map a joined picker candidate row directly into a picker selection.
 * Convenience wrapper around `toInventoryPickerSelection` for query
 * consumers that have already received the joined shape.
 */
export function pickerCandidateRowToSelection(
  row: InventoryPickerCandidateRow,
): InventoryPickerSelection {
  const detail: InventoryMedicineDetailRow | null =
    row.med_generic_name === null &&
    row.med_brand_name === null &&
    row.med_strength === null &&
    row.med_dosage_form === null &&
    row.med_route === null &&
    row.med_default_instruction === null
      ? null
      : {
          id: '', // not selected by the join — not needed by the mapper
          inventory_item_id: row.id,
          drug_classification: row.med_drug_classification,
          brand_name: row.med_brand_name,
          generic_name: row.med_generic_name,
          manufacturer: row.med_manufacturer,
          indication: row.med_indication,
          strength: row.med_strength,
          dosage_form: row.med_dosage_form,
          route: row.med_route,
          precaution_1: null,
          precaution_2: null,
          default_instruction: row.med_default_instruction,
          default_dosage: row.med_default_dosage,
          usage: row.med_usage,
          dose: row.med_dose,
          dose_unit: row.med_dose_unit,
          frequency: row.med_frequency,
          duration: row.med_duration,
          created_at: null,
          updated_at: null,
        };
  return toInventoryPickerSelection(row, detail);
}
